import type { DeploymentReference, PublicV2Purchase } from "./public-v2";

type RecordValue = Record<string, unknown>;
function isRecord(value: unknown): value is RecordValue {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function sameDeployment(left: unknown, right: DeploymentReference): boolean {
  if (!isRecord(left)) return false;
  return left.schemaVersion === right.schemaVersion && left.protocol === right.protocol && left.chainId === right.chainId &&
    left.registryAddress === right.registryAddress && left.deploymentBlockNumber === right.deploymentBlockNumber &&
    left.deploymentBlockHash === right.deploymentBlockHash && left.deploymentTransactionHash === right.deploymentTransactionHash &&
    left.registryRuntimeCodeHash === right.registryRuntimeCodeHash && left.sourceCommitment === right.sourceCommitment;
}
function address(value: unknown): value is string { return typeof value === "string" && /^0x[0-9a-f]{40}$/i.test(value); }

/** Ephemeral display overlay only; persisted launch and funding records are never changed. */
export function isVerifiedRefundedTerminalPurchase(input: {
  state: string;
  purchase: PublicV2Purchase | null | undefined;
  record: unknown;
  deployment: DeploymentReference;
}): boolean {
  const { state, purchase, record, deployment } = input;
  if (state !== "data" || !purchase || !isRecord(record) || record.phase !== "queued" ||
      !sameDeployment(record.deployment, deployment) ||
      record.namespace !== `${deployment.chainId}:${deployment.registryAddress.toLowerCase()}` ||
      purchase.namespace !== `${deployment.chainId}:${deployment.registryAddress}`) return false;
  const payment = record.paymentEvidence;
  const reservation = record.reservation;
  const voucher = isRecord(reservation) ? reservation.voucher : null;
  const agentId = isRecord(payment) ? payment.agentId : null;
  const reservationId = isRecord(reservation) ? reservation.reservationId : null;
  const localPayer = record.payer;
  const voucherPayer = isRecord(voucher) ? voucher.payer : null;
  const voucherCreator = isRecord(voucher) ? voucher.creator : null;
  if (typeof agentId !== "string" || !/^(0|[1-9][0-9]*)$/.test(agentId) ||
      typeof reservationId !== "string" || !/^0x[0-9a-f]{64}$/i.test(reservationId) ||
      !address(localPayer) || !address(voucherPayer) || !address(voucherCreator)) return false;
  return purchase.phase === "refunded" && purchase.refundCredited === true && purchase.capacityReleased === true &&
    purchase.agentId === agentId && purchase.reservationId.toLowerCase() === reservationId.toLowerCase() &&
    purchase.payer.toLowerCase() === localPayer.toLowerCase() && voucherPayer.toLowerCase() === localPayer.toLowerCase() &&
    purchase.creator.toLowerCase() === voucherCreator.toLowerCase();
}
